import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Heart, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* HERZBEWEGUNG palette — matches deck theme (Teal-Dark-Green + Gold + Cream) */
const M = {
  teal: "#0F4F4F",
  tealDeep: "#0A3838",
  tealMid: "#1B6B6B",
  cream: "#F5EBE0",
  creamSoft: "#FBF4EA",
  white: "#FFFFFF",
  ink: "#0F3D3E",
  inkSoft: "#3A5658",
  gold: "#D4AF37",
  goldSoft: "#E8C566",
};

export default function Herzbotschafter() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", country: "", motivation: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent) return setError("Bitte stimme der Aufnahme in die Bewegung zu.");
    if (form.motivation.trim().length < 10) return setError("Bitte schreibe uns kurz, was dich bewegt (min. 10 Zeichen).");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/herz/herzbotschafter`, form);
      if (data.ok) setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Etwas ging schief. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ background: M.tealDeep }} data-testid="herzbotschafter-page">
      <div className="container-sat max-w-3xl">
        <Link
          to="/herzbewegung/deck"
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase mb-6 transition hover:translate-x-[-2px]"
          style={{ color: M.gold }}
          data-testid="herzbotschafter-back"
        >
          <ArrowLeft size={12} /> Zurück zum Deck
        </Link>

        <div
          className="rounded-2xl p-8 md:p-12 border"
          style={{
            background: `linear-gradient(135deg, ${M.cream} 0%, ${M.creamSoft} 60%, ${M.cream} 100%)`,
            borderColor: `${M.gold}55`,
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-3" style={{ color: M.gold }}>
              WERDE HERZBOTSCHAFTER
            </div>
            <h1 className="serif font-light leading-tight tracking-tight" style={{ fontSize: "clamp(2rem,4vw,3.4rem)", color: M.ink }}>
              Ein Mensch. <span style={{ color: M.tealMid }}>Ein Herz.</span> <span style={{ color: M.gold }}>Eine Wirkung.</span>
            </h1>
            <p className="serif italic mt-3 max-w-xl mx-auto" style={{ color: M.inkSoft, fontSize: "1rem" }}>
              Die Welt wartet auf dich. Sag Ja — und werde Teil einer Bewegung, die mit Herz die Welt neu gestaltet.
            </p>
          </div>

          {success ? (
            <div className="text-center py-8" data-testid="herzbotschafter-success">
              <div
                className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ background: M.tealMid }}
              >
                <CheckCircle2 size={32} style={{ color: M.white }} />
              </div>
              <h2 className="serif font-light text-2xl md:text-3xl mb-2" style={{ color: M.ink }}>
                Willkommen in der Bewegung, <span style={{ color: M.gold }}>{form.name.split(" ")[0]}</span>.
              </h2>
              <p className="text-sm mb-4 max-w-md mx-auto" style={{ color: M.inkSoft }}>
                Du hast Ja gesagt. Eine Willkommens-Nachricht mit deinen nächsten Schritten ist unterwegs zu <strong>{form.email}</strong>.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <Link
                  to="/herzbewegung"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
                  style={{ background: M.gold, color: M.tealDeep }}
                  data-testid="herzbotschafter-go-live-map"
                >
                  Zur Live-Karte <Heart size={14} fill={M.tealDeep} />
                </Link>
                <button
                  onClick={() => nav("/herzbewegung/deck")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm"
                  style={{ borderColor: `${M.tealMid}55`, color: M.tealMid }}
                >
                  Zurück zum Deck
                </button>
              </div>
              <p className="mt-8 serif italic text-sm" style={{ color: M.gold }}>&bdquo;Love is Transformation.&ldquo;</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="herzbotschafter-form">
              {/* Name */}
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase font-bold mb-1.5" style={{ color: M.inkSoft }}>
                  Dein Name
                </label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Vor- und Nachname"
                  className="w-full px-4 py-3 rounded-lg border bg-white/70 focus:outline-none focus:ring-2 transition"
                  style={{ borderColor: `${M.tealMid}44`, color: M.ink }}
                  data-testid="herzbotschafter-input-name"
                />
              </div>

              {/* Email + Country grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase font-bold mb-1.5" style={{ color: M.inkSoft }}>
                    E-Mail
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="du@herz.ch"
                    className="w-full px-4 py-3 rounded-lg border bg-white/70 focus:outline-none focus:ring-2 transition"
                    style={{ borderColor: `${M.tealMid}44`, color: M.ink }}
                    data-testid="herzbotschafter-input-email"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase font-bold mb-1.5" style={{ color: M.inkSoft }}>
                    Land (optional)
                  </label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={update("country")}
                    placeholder="Schweiz"
                    className="w-full px-4 py-3 rounded-lg border bg-white/70 focus:outline-none focus:ring-2 transition"
                    style={{ borderColor: `${M.tealMid}44`, color: M.ink }}
                    data-testid="herzbotschafter-input-country"
                  />
                </div>
              </div>

              {/* Motivation */}
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase font-bold mb-1.5" style={{ color: M.inkSoft }}>
                  Was bewegt dich?
                </label>
                <textarea
                  required
                  minLength={10}
                  maxLength={1000}
                  rows={4}
                  value={form.motivation}
                  onChange={update("motivation")}
                  placeholder="Warum möchtest du Herzbotschafter:in werden? Was ist dein Herzensanliegen?"
                  className="w-full px-4 py-3 rounded-lg border bg-white/70 focus:outline-none focus:ring-2 transition resize-none"
                  style={{ borderColor: `${M.tealMid}44`, color: M.ink }}
                  data-testid="herzbotschafter-input-motivation"
                />
                <div className="text-[10px] text-right mt-1" style={{ color: M.inkSoft }}>
                  {form.motivation.length}/1000
                </div>
              </div>

              {/* Consent */}
              <label className="flex items-start gap-3 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={update("consent")}
                  className="mt-1 shrink-0"
                  data-testid="herzbotschafter-input-consent"
                />
                <span className="text-[12px] leading-snug" style={{ color: M.inkSoft }}>
                  Ich möchte Teil der Herzbewegung werden und stimme zu, gelegentlich Impulse, Missionen und Updates per E-Mail zu erhalten. (Abbestellen jederzeit möglich.)
                </span>
              </label>

              {error && (
                <div
                  className="rounded-lg px-4 py-3 text-sm border"
                  style={{ background: "#FEECEC", borderColor: "#E88B8B", color: "#8C3B3B" }}
                  data-testid="herzbotschafter-error"
                >
                  {error}
                </div>
              )}

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full text-base font-semibold transition disabled:opacity-60"
                  style={{
                    background: `linear-gradient(90deg, ${M.tealMid}, ${M.gold})`,
                    color: M.white,
                    letterSpacing: "0.1em",
                  }}
                  data-testid="herzbotschafter-submit"
                >
                  {loading ? "Sende ..." : <>Ja, ich werde Herzbotschafter:in <Send size={16} /></>}
                </button>
                <p className="mt-3 text-center serif italic text-xs" style={{ color: M.tealMid }}>
                  &bdquo;Zünde Liebe an. Verbreite Hoffnung. Verändere die Welt.&ldquo;
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
